import React, { ReactNode } from 'react';
import { styled, Box } from '@mui/material';

const TableContainer = styled('table', {
  shouldForwardProp: (prop) =>
    prop !== 'noBorderTopRadius' && prop !== 'noBorderBottomRadius',
})<{
  noBorderTopRadius?: boolean;
  noBorderBottomRadius?: boolean;
}>(({ theme, noBorderTopRadius = false, noBorderBottomRadius = false }) => ({
  display: 'table',
  tableLayout: 'fixed',
  width: '100%',
  fontSize: theme.fontSizes?.sm,
  color: theme.palette.text.darkBodyText,
  backgroundColor: theme.palette.background.paper,
  textAlign: 'left',
  borderCollapse: 'separate',
  borderSpacing: 0,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: noBorderTopRadius ? '0' : theme.shape.borderRadius,
  borderTopRightRadius: noBorderTopRadius ? '0' : theme.shape.borderRadius,
  borderBottomLeftRadius: noBorderBottomRadius ? '0' : theme.shape.borderRadius,
  borderBottomRightRadius: noBorderBottomRadius
    ? '0'
    : theme.shape.borderRadius,
}));

const TableHeading = styled('th')<{
  align?: 'left' | 'center';
  headerColor?: 'grey' | 'white';
}>(({ theme, align = 'left', headerColor }) => ({
  padding: theme.spacing(3, 4),
  borderBottom: `2px solid ${theme.palette.divider}`,
  verticalAlign: 'top',
  backgroundColor:
    headerColor === 'white'
      ? theme.palette.background.paper
      : theme.palette.background.header,
  minWidth: 140,
  textAlign: align,
}));

const TableCell = styled('td', {
  shouldForwardProp: (prop) => prop !== 'hasGroupPadding',
})<{ hasGroupPadding?: boolean }>(({ theme, hasGroupPadding = true }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.darkBodyText,
  padding: theme.spacing(2, 4),
  '&.first-of-group': {
    paddingTop: hasGroupPadding ? theme.spacing(3) : theme.spacing(2),
  },
  '&.last-of-group': {
    paddingBottom: hasGroupPadding ? theme.spacing(3) : theme.spacing(2),
  },
  '&.first-row': {
    paddingTop: hasGroupPadding ? theme.spacing(3) : theme.spacing(2),
  },
  '&.last-row': {
    paddingBottom: hasGroupPadding ? theme.spacing(3) : theme.spacing(2),
  },
}));

const GroupSeparator = styled('tr')(({ theme }) => ({
  '& td': {
    borderTop: `2px solid ${theme.palette.divider}`,
  },
}));

const Separator = styled('tr')(({ theme }) => ({
  '& td': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const TableWrapper = styled(Box)({
  overflowX: 'auto',
});

type SectionedTableColumn<T> = {
  key: string;
  label: string;
  render?: (row: T, index: number) => ReactNode;
};

type SectionedTableProps<T> = {
  columns: Array<SectionedTableColumn<T>>;
  rows: Array<T>;
  title?: string;
  centerTitle?: boolean;
  headerColor?: string;
  showHeader?: boolean;
  noBorderTopRadius?: boolean;
  noBorderBottomRadius?: boolean;
  showSeparator?: boolean;
  hasGroupPadding?: boolean;
};

export type Row = {
  key: string;
  label: string;
  group: string;
  tooltip?: string;
};

const SectionedTable = <T extends Row>(props: SectionedTableProps<T>) => {
  const {
    columns,
    rows,
    title,
    centerTitle = false,
    headerColor = 'grey',
    showHeader = true,
    noBorderTopRadius = false,
    noBorderBottomRadius = false,
    showSeparator = true,
    hasGroupPadding = true,
  } = props;
  // Group separator logic
  const groupSeparatorIndices = rows.reduce<Array<number>>(
    (acc, row, index) => {
      if (index > 0 && row.group !== rows[index - 1].group) {
        acc.push(index - 1);
      }
      return acc;
    },
    [],
  );

  const isFirstRow = (index: number) => index === 0;
  const isLastRow = (index: number) => index === rows.length - 1;
  const isFirstInGroup = (index: number) =>
    groupSeparatorIndices?.includes(index - 1);
  const isLastInGroup = (index: number) =>
    groupSeparatorIndices?.includes(index);

  return (
    <TableWrapper>
      <TableContainer
        noBorderTopRadius={noBorderTopRadius}
        noBorderBottomRadius={noBorderBottomRadius}
      >
        <thead>
          {title && (
            <tr>
              <TableHeading
                colSpan={columns.length}
                align={centerTitle ? 'center' : 'left'}
              >
                {title}
              </TableHeading>
            </tr>
          )}
          {showHeader && (
            <tr>
              {columns.map((column) => (
                <TableHeading key={column.key} headerColor={headerColor}>
                  {column.label}
                </TableHeading>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <React.Fragment key={`row-${index}`}>
              <tr>
                {columns.map((column) => (
                  <TableCell
                    key={`cell-${index}-${column.key}`}
                    hasGroupPadding={hasGroupPadding}
                    className={`
                      ${isFirstRow(index) ? 'first-row' : ''}
                      ${isLastRow(index) ? 'last-row' : ''}
                      ${isFirstInGroup(index) ? 'first-of-group' : ''}
                      ${isLastInGroup(index) ? 'last-of-group' : ''}
                    `}
                  >
                    {column.render?.(row, index) ?? row[column.key]}
                  </TableCell>
                ))}
              </tr>
              {isLastInGroup(index) && !isLastRow(index) && (
                <GroupSeparator>
                  <td colSpan={columns.length} />
                </GroupSeparator>
              )}
              {showSeparator && !isLastRow(index) && (
                <Separator>
                  <td colSpan={columns.length} />
                </Separator>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </TableContainer>
    </TableWrapper>
  );
};

export default SectionedTable;
